import { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import BreadCrumbs from "../../Components/BreadCrumbs/BreadCrumbs";
import SingleSelect from "../../Components/Filters/SingleSelect";
import {
  checkArraysAreEqual,
  FilterSeparator,
  isFilterApplied
} from "../../Utils/Constants/Constants";
import { setLiveFeedAutoSwitchTime } from "../../Redux/surveillance/action";
import { ArrowIcon } from "../../Assets/assets";
import { isValidArray, isValidObject } from "../../Services/validators";
import FilterCarousal from "../../Components/FilterCarousal/FilterCarousal";
import MultipleSelect from "../../Components/Filters/MultipleSelect";
import {
  defaultLiveFeedFilter,
  liveFeedFilterData
} from "../../Utils/Constants/LiveFeed";
import Button from "../../Components/Button/Button";
import { setTableFilter } from "../../Redux/table/action";

function LiveFeed(props) {
  const [feedShowCount, setFeedShowCount] = useState({
    horizontal: 2,
    vertical: 2
  });
  const [currentFeedIndexes, setCurrentIndexes] = useState(
    Array(feedShowCount.horizontal * feedShowCount.vertical).fill(null)
  );
  const [feeds, setFeeds] = useState([]);
  const [disableNextButton, setDisableNextButton] = useState(true);
  const [disablePreviousButton, setDisablePreviousButton] = useState(true);

  const autoSwitchTimer = useRef(null);
  const [selectedFilters, setSelectedFilters] = useState(
    props.table.liveFeed.filter
  );

  const autoSwitchOptions = [
    "5 seconds",
    "10 seconds",
    "30 seconds",
    "60 seconds"
  ];

  useEffect(() => {
    if (props.surveillance?.liveFeedAutoSwitch === null) {
      props.setLiveFeedAutoSwitchTime(autoSwitchOptions[2]);
    }
    // eslint-disable-next-line
  }, [props.surveillance?.liveFeedAutoSwitch]);

  useEffect(() => {
    let streamDevices = [];
    if (isValidObject(props.surveillance?.devices)) {
      streamDevices = Object.values(props.surveillance?.devices);
      streamDevices = streamDevices.filter((data) => {
        const filterData = props.table.liveFeed.filter;
        if (!data.stream) {
          return false;
        }

        // PCTNo filter
        if (
          filterData?.PCTNo?.length > 0 &&
          !filterData?.PCTNo.includes(data.location?.PCTNo)
        ) {
          const PCTNos = filterData.PCTNo.map((value) => value.split(" -")[0]);
          if (!PCTNos.includes(data.location?.PCTNo)) {
            return false;
          }
        }

        // ward filter
        if (
          filterData?.ward?.length > 0 &&
          !filterData?.ward.includes(data.location?.ward)
        ) {
          return false;
        }

        // zone filter
        if (
          filterData?.zone?.length > 0 &&
          !filterData?.zone.includes(data.location?.zone)
        ) {
          return false;
        }

        return true;
      });

      streamDevices = streamDevices?.sort((a, b) => {
        const fieldA = a?.location?.PCTNo ?? "";
        const fieldB = b?.location?.PCTNo ?? "";
        return fieldA.localeCompare(fieldB, undefined, { numeric: true });
      });

      setFeeds(streamDevices);
    } else {
      setFeeds([]);
    }

    // eslint-disable-next-line
  }, [props.surveillance?.devices, props.table.liveFeed.filter]);

  function switchNextFeeds() {
    let totalFeedShowCount = feedShowCount.horizontal * feedShowCount.vertical;

    if (feeds.length === 1) {
      setFeedShowCount({ horizontal: 1, vertical: 1 });
      totalFeedShowCount = 1;
    } else if (feedShowCount.horizontal !== 2 || feedShowCount.vertical !== 2) {
      setFeedShowCount({ horizontal: 2, vertical: 2 });
      totalFeedShowCount = 4;
    }

    const currentFeedLastIndex =
      currentFeedIndexes[currentFeedIndexes.length - 1];

    let nextFeedIndexes = Array(
      feeds.length === 1 ? 1 : totalFeedShowCount
    ).fill(null);

    if (
      currentFeedLastIndex === null ||
      currentFeedLastIndex === feeds?.length - 1
    ) {
      nextFeedIndexes = nextFeedIndexes.map((v, index) => {
        if (feeds?.length - 1 >= index) {
          return index;
        } else {
          return null;
        }
      });
    } else {
      nextFeedIndexes = nextFeedIndexes.map((v, index) => {
        const nextIndex = currentFeedLastIndex + index + 1;
        if (nextIndex <= feeds?.length - 1) {
          return nextIndex;
        } else {
          return null;
        }
      });
    }

    setCurrentIndexes(nextFeedIndexes);
  }

  function switchPreviousFeeds() {
    if (disablePreviousButton) {
      return;
    }
    const totalFeedShowCount =
      feedShowCount.horizontal * feedShowCount.vertical;

    let previousFeedIndexes = Array(totalFeedShowCount).fill(null);

    previousFeedIndexes = previousFeedIndexes.map((v, index) => {
      return currentFeedIndexes[0] - (index + 1);
    });

    previousFeedIndexes.reverse();

    setCurrentIndexes(previousFeedIndexes);
  }

  useEffect(() => {
    setDisablePreviousButton(
      currentFeedIndexes[0] === 0 || currentFeedIndexes[0] === null
    );
    // eslint-disable-next-line
  }, [currentFeedIndexes]);

  useEffect(() => {
    if (isValidArray(feeds)) {
      switchNextFeeds();
    }
    setDisableNextButton(feeds.length <= 4);
    // eslint-disable-next-line
  }, [feeds]);

  useEffect(() => {
    const totalFeedShowCount =
      feedShowCount.horizontal * feedShowCount.vertical;
    if (!isValidArray(feeds) || totalFeedShowCount > feeds?.length) {
      return;
    }

    if (props.surveillance?.liveFeedAutoSwitch) {
      let time = 30000;
      const liveFeedAutoSwitch = props.surveillance?.liveFeedAutoSwitch?.split(
        " "
      );

      if (liveFeedAutoSwitch[1] === "seconds") {
        time = liveFeedAutoSwitch[0] * 1000;
      } else if (liveFeedAutoSwitch[1] === "minutes") {
        time = liveFeedAutoSwitch[0] * 1000 * 60;
      }

      if (autoSwitchTimer.current) {
        clearTimeout(autoSwitchTimer.current);
      }
      autoSwitchTimer.current = setTimeout(switchNextFeeds, time);
    }

    // eslint-disable-next-line
  }, [
    currentFeedIndexes,
    feedShowCount,
    feeds,
    props.surveillance?.liveFeedAutoSwitch
  ]);

  const handleResetClick = (key) => {
    if (
      !isValidArray(props.table.liveFeed.filter[key]) ||
      !isValidArray(selectedFilters[key])
    ) {
      return;
    }
    if (isValidArray(selectedFilters[key])) {
      setSelectedFilters({
        ...selectedFilters,
        [key]: defaultLiveFeedFilter[key]
      });
    }

    if (isValidArray(props.table.liveFeed.filter[key])) {
      props.setTableFilter("LIVE_FEED", {
        ...props.table.liveFeed.filter,
        [key]: defaultLiveFeedFilter[key]
      });
    }
  };

  const resetAllClick = () => {
    props.setTableFilter("LIVE_FEED", defaultLiveFeedFilter);
    setSelectedFilters(defaultLiveFeedFilter);
  };

  const disableFilterApplyButton = () => {
    for (const key in selectedFilters) {
      if (
        !checkArraysAreEqual(
          selectedFilters[key],
          props.table.liveFeed.filter[key]
        )
      ) {
        return false;
      }
    }
    return true;
  };

  return (
    <>
      <div className="inherit-parent-height inherit-parent-width position-relative overflow-hidden">
        <div
          className="display-flex flex-justify-content-space-between flex-align-items-center"
          style={{ height: "37px" }}
        >
          <BreadCrumbs />
          {feedShowCount.horizontal * feedShowCount.vertical < feeds.length && (
            <div className="display-flex flex-align-items-center font-color-tertiaryColor">
              <div className=" padding-right-medium">
                <SingleSelect
                  disabled={false}
                  loading={false}
                  data-cy="AUTO-SWITCH-EVERY"
                  data={{
                    key: "autoSwitchEvery",
                    multiple: false,
                    options: autoSwitchOptions,
                    title: "AUTO-SWITCH EVERY"
                  }}
                  // onResetClick={(key) => {
                  //   // handleResetClick(key);
                  // }}
                  setSelectedFilters={(data) => {
                    if (
                      !props.surveillance.loading &&
                      data.autoSwitchEvery !== "All"
                    ) {
                      props.setLiveFeedAutoSwitchTime(data.autoSwitchEvery);
                    }
                  }}
                  selectedFilters={{
                    autoSwitchEvery: props.surveillance.liveFeedAutoSwitch
                  }}
                  appliedFilters={{
                    autoSwitchEvery: props.surveillance.liveFeedAutoSwitch
                  }}
                  selected={[props.surveillance.liveFeedAutoSwitch]}
                />
              </div>
              <FilterSeparator />
              <div className=" display-flex flex-align-items-center">
                <div
                  data-cy="PREVIOUS-PAGE-BUTTON"
                  className={`
                  ${
                    disablePreviousButton ? "cursor-no-drop" : "cursor-pointer"
                  } display-flex flex-align-items-center padding-horizontal-default`}
                  style={{
                    color: disablePreviousButton
                      ? "var(--tertiaryColor5)"
                      : "var(--tertiaryColor6)"
                  }}
                  onClick={() => {
                    if (!disablePreviousButton) {
                      switchPreviousFeeds();
                    }
                  }}
                >
                  <div
                    style={{
                      transform: "rotate(90deg)"
                    }}
                  >
                    <ArrowIcon
                      height="20"
                      width="20"
                      color={
                        disablePreviousButton
                          ? "var(--tertiaryColor5)"
                          : "var(--tertiaryColor6)"
                      }
                    />
                  </div>

                  <div className=" padding-left-default">Prev</div>
                </div>

                <div
                  data-cy="NEXT-PAGE-BUTTON"
                  className={`
                  ${
                    disableNextButton ? "cursor-no-drop" : "cursor-pointer"
                  } display-flex flex-align-items-center padding-horizontal-default`}
                  style={{
                    color: disableNextButton
                      ? "var(--tertiaryColor5)"
                      : "var(--tertiaryColor6)"
                  }}
                  onClick={() => {
                    if (!disableNextButton) {
                      switchNextFeeds();
                    }
                  }}
                >
                  <div className=" padding-right-default">Next</div>

                  <div
                    style={{
                      transform: "rotate(270deg)"
                    }}
                  >
                    <ArrowIcon
                      height="20"
                      width="20"
                      color={
                        disableNextButton
                          ? "var(--tertiaryColor5)"
                          : "var(--tertiaryColor6)"
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="position-relative margin-top-larger display-flex">
          <>
            <FilterCarousal>
              <div className="display-flex flex-align-items-center">
                <MultipleSelect
                  loading={false}
                  data-cy="PCT-NO-MULTI"
                  data={liveFeedFilterData(props.surveillance.devices)[0]}
                  onResetClick={(key) => {
                    handleResetClick(key);
                  }}
                  disabled={false}
                  setSelectedFilters={setSelectedFilters}
                  selectedFilters={selectedFilters}
                  appliedFilters={props.table.liveFeed.filter}
                  selected={selectedFilters["PCTNo"]}
                />

                <FilterSeparator />

                <MultipleSelect
                  loading={false}
                  data-cy="WARD-MULTI"
                  data={liveFeedFilterData(props.surveillance.devices)[1]}
                  onResetClick={(key) => {
                    handleResetClick(key);
                  }}
                  setSelectedFilters={setSelectedFilters}
                  selectedFilters={selectedFilters}
                  appliedFilters={props.table.liveFeed.filter}
                  selected={selectedFilters["ward"]}
                />

                <FilterSeparator />

                <MultipleSelect
                  loading={false}
                  data-cy="ZONE-MULTI"
                  data={liveFeedFilterData(props.surveillance.devices)[2]}
                  onResetClick={(key) => {
                    handleResetClick(key);
                  }}
                  setSelectedFilters={setSelectedFilters}
                  selectedFilters={selectedFilters}
                  appliedFilters={props.table.liveFeed.filter}
                  selected={selectedFilters["zone"]}
                />
              </div>
            </FilterCarousal>
          </>

          {isFilterApplied(props.table.liveFeed.filter, "LIVE_FEED")
            .validation ||
          isFilterApplied(selectedFilters, "LIVE_FEED").validation ? (
            <div
              style={{ width: "10%" }}
              className={`display-flex flex-justify-content-space-between`}
            >
              <div
                style={{
                  width: "60%"
                }}
              >
                <Button
                  data-cy={`FILTER-APPLY-BUTTON${false ? "-LOADING" : ""}`}
                  text="Apply"
                  boxShadow={false}
                  variant="filter"
                  loading={false}
                  className="inherit-parent-width padding-left-large padding-right-large"
                  disabled={disableFilterApplyButton()}
                  onClick={() => {
                    props.setTableFilter("LIVE_FEED", selectedFilters);
                    setCurrentIndexes(
                      Array(
                        feedShowCount.horizontal * feedShowCount.vertical
                      ).fill(null)
                    );
                  }}
                />
              </div>
              <div
                style={{
                  width: "40%"
                }}
              >
                <Button
                  data-cy="FILTER-RESET-BUTTON"
                  variant="transparent"
                  text="Clear"
                  boxShadow={false}
                  className="inherit-parent-width padding-left-large padding-right-large"
                  onClick={resetAllClick}
                />
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
        {isValidArray(feeds) ? (
          <div
            className=" padding-top-larger"
            style={{
              height: `calc(100% - 98px)`,
              display: "grid",
              gridTemplateColumns: `repeat(${feedShowCount.horizontal}, 1fr)`,
              gridTemplateRows: `repeat(${feedShowCount.vertical}, 1fr)`,
              gap: "24px"
            }}
          >
            {currentFeedIndexes?.map((feedIndex) =>
              feedIndex !== null ? (
                <div data-cy={`LIVE-FEED-${feeds[feedIndex]?.location?.PCTNo}`}>
                  <div
                    className=" display-flex flex-justify-content-space-between padding-default"
                    style={{
                      position: "relative",
                      zIndex: "20",
                      color: "white"
                    }}
                  >
                    <div></div>
                    <div
                      className=" padding-default"
                      style={{
                        background: "rgba(0, 0, 0, 0.7)"
                      }}
                    >
                      PCT# {feeds[feedIndex]?.location?.PCTNo || "N/A"}
                    </div>
                  </div>
                  <iframe
                    title={`liveFeed${feedIndex}`}
                    src={feeds[feedIndex]?.stream}
                    width="100%"
                    height="100%"
                    style={{
                      border: "2px solid var(--tertiaryColor4)",
                      position: "relative",
                      top: "-53px"
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "2px solid var(--tertiaryColor4)"
                  }}
                ></div>
              )
            )}
          </div>
        ) : (
          <>
            {isFilterApplied(props.table.liveFeed.filter, "LIVE_FEED")
              .validation ? (
              <NoResultsFallback resetAllClick={resetAllClick} />
            ) : (
              <div
                className=" inherit-parent-width display-flex flex-direction-column flex-align-items-center flex-justify-content-center"
                style={{ height: `calc(100% - 98px)` }}
              >
                <div className="inherit-parent-width white-space-nowrap font-color-tertiaryColor font-size-larger flex-justify-content-center">
                  No feeds available
                </div>
                <div className="white-space-nowrap font-size-small padding-top-default font-color-tertiaryColor6">
                  CHECK BACK AFTER SOMETIME
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

const mapStateToProps = function(state) {
  return {
    locations: state.locations,
    surveillance: state.surveillance,
    table: state.table
  };
};

const mapDispatchToProps = function() {
  return {
    setLiveFeedAutoSwitchTime: (time) => setLiveFeedAutoSwitchTime(time),
    setTableFilter: (tableName, data) => setTableFilter(tableName, data)
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(LiveFeed);

const NoResultsFallback = (props) => {
  return (
    <div
      style={{ height: `calc(100% - 98px)` }}
      className="inherit-parent-width  display-flex flex-direction-column flex-align-items-center flex-justify-content-center"
    >
      <div className="inherit-parent-width white-space-nowrap font-color-tertiaryColor font-size-larger flex-justify-content-center">
        No results found
      </div>
      <div className="white-space-nowrap font-size-small padding-top-default font-color-tertiaryColor6">
        TRY DIFFERENT FILTER OR
      </div>
      <div
        style={{
          width: "150px"
        }}
        className="padding-top-medium"
      >
        <Button
          boxShadow={false}
          onClick={props.resetAllClick}
          text="Reset All"
          variant="danger"
        />
      </div>
    </div>
  );
};
